import { useEffect } from "react";
import { type User, onAuthChange } from "@/features/auth/services/firebase";
import { identifyUser } from "@/services/posthog";
import type { UserProfile } from "@/features/auth/contexts/auth/context";
import {
  clearDevAuthToken,
  debugLog,
  exposeDevAuthToken,
} from "./authSessionHelpers";
interface UseAuthStateParams {
  isConfigured: boolean;
  fetchProfile: (firebaseUser: User) => Promise<UserProfile | null>;
  setUser: (user: User | null) => void;
  setProfile: (profile: UserProfile | null) => void;
  setLoading: (loading: boolean) => void;
  authGenerationRef: React.MutableRefObject<number>;
}

export function useAuthState({
  isConfigured,
  fetchProfile,
  setUser,
  setProfile,
  setLoading,
  authGenerationRef,
}: UseAuthStateParams): void {
  useEffect(() => {
    debugLog("Auth effect running", { isConfigured });
    if (!isConfigured) {
      debugLog("Firebase not configured, skipping auth setup");
      setLoading(false);
      return;
    }

    const handleAuthChange = async (firebaseUser: User | null) => {
      const currentGen = ++authGenerationRef.current;
      debugLog("Auth state changed", { hasUser: !!firebaseUser, email: firebaseUser?.email, gen: currentGen });
      setUser(firebaseUser);
      if (firebaseUser) {
        debugLog("User signed in, fetching profile", { gen: currentGen });
        await exposeDevAuthToken(firebaseUser);
        if (authGenerationRef.current !== currentGen) return;
        const userProfile = await fetchProfile(firebaseUser);
        if (authGenerationRef.current !== currentGen) return;
        debugLog("Setting profile", { hasProfile: !!userProfile, userId: userProfile?.id, gen: currentGen });
        setProfile(userProfile);
        if (userProfile) {
          await identifyUser(userProfile.id, {
            hasTeam: !!userProfile.teams?.some(team => !team.isPersonal),
            locale: userProfile.appLanguage,
          });
        }
      } else {
        debugLog("User signed out, clearing profile", { gen: currentGen });
        if (authGenerationRef.current !== currentGen) return;
        setProfile(null);
        await identifyUser(null);
        clearDevAuthToken();
      }
      debugLog("Auth state change complete, setting loading=false", { gen: currentGen });
      setLoading(false);
    };

    const unsubscribe = onAuthChange((firebaseUser) => {
      void handleAuthChange(firebaseUser);
    });
    return unsubscribe;
  }, [fetchProfile, isConfigured, setLoading, setProfile, setUser, authGenerationRef]);
}
